import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { 
  User, 
  Clock, 
  ChevronRight,
  Store,
  Radio,
  MessageCircle,
  Package,
  MapPin,
  DollarSign,
  Loader,
  Lock
} from 'lucide-react';
import MyDelivery from './MyDelivery';
import ControlTower from './ControlTower';
import TimeSlotSelector from './TimeSlotSelector';
import StoreInfo from './StoreInfo';

const API_URL = process.env.REACT_APP_API_URL;

function FinalClientMain() {
  const { deliveryId } = useParams();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeComponent, setActiveComponent] = useState(null);
  const [error, setError] = useState(null);

  const fetchDeliveryData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/delivery/${deliveryId}`);
      setDelivery(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching delivery:', err);
      setError('No se pudo cargar la información de tu entrega');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (deliveryId) {
      fetchDeliveryData();
    }
  }, [deliveryId]);

  const handleBackToMain = () => {
    setActiveComponent(null);
    fetchDeliveryData();
  };

  const openWhatsApp = () => {
    const phone = '51977802104';
    const message = encodeURIComponent(`Hola, necesito ayuda con mi entrega #${deliveryId}`);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  // Check if time slot selection is available
  const isTimeSlotAvailable = delivery?.status === 'pending';

  const menuItems = [
    {
      id: 'MyDelivery',
      label: 'Mi Entrega',
      icon: Package,
      onClick: () => setActiveComponent('MyDelivery'),
      color: 'from-brand-primary to-brand-gold',
      available: true, // Always available
    },
    {
      id: 'ControlTower',
      label: delivery?.latitude ? 'Seguir Conductor' : 'Confirmar Ubicación',
      icon: delivery?.latitude ? Radio : MapPin,
      onClick: () => setActiveComponent('ControlTower'),
      color: 'from-brand-green to-brand-gold',
      available: true, // Always available
    },
    {
      id: 'Time',
      label: 'Horario de Entrega',
      icon: Clock,
      onClick: () => isTimeSlotAvailable ? setActiveComponent('Time') : null,
      color: 'from-brand-green to-brand-primary',
      available: isTimeSlotAvailable,
      disabledMessage: 'Solo disponible para entregas pendientes'
    },
    {
      id: 'Chat',
      label: 'Chat con Fast Max',
      icon: MessageCircle,
      onClick: openWhatsApp,
      color: 'from-brand-primary to-brand-green',
      available: true, // Always available
    },
    {
      id: 'Store',
      label: 'Información Tienda',
      icon: Store,
      onClick: () => setActiveComponent('Store'),
      color: 'from-brand-gold to-brand-primary',
      available: true, // Always available
    },
  ];

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-montserrat">Cargando tu entrega...</p>
        </div>
      </div>
    );
  }

  if (error || !delivery) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4">
        <div className="text-center max-w-md">
          <Package className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2 font-montserrat">
            Entrega no encontrada
          </h2>
          <p className="text-slate-600 font-montserrat">
            {error || 'No pudimos encontrar información sobre esta entrega'}
          </p>
        </div>
      </div>
    );
  }

  // Render active component
  if (activeComponent === 'MyDelivery') {
    return <MyDelivery delivery={delivery} onBack={handleBackToMain} />;
  }

  if (activeComponent === 'ControlTower') {
    return <ControlTower delivery={delivery} onBack={handleBackToMain} />;
  }

  if (activeComponent === 'Time') {
    return <TimeSlotSelector delivery={delivery} onBack={handleBackToMain} />;
  }

  if (activeComponent === 'Store') {
    return <StoreInfo delivery={delivery} onBack={handleBackToMain} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <Package className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat tracking-tight">
                  FAST MAX
                </h1>
                <p className="text-xs text-white/90 font-montserrat">
                  Seguimiento de entrega
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
          
          {/* Profile/Delivery Info Card */}
          <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-extrabold text-brand-primary font-montserrat mb-1">
                  {delivery.recipient_name}
                </h2>
                <div className="flex items-start gap-2 text-sm text-slate-600 font-montserrat mb-2">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <p 
                    className="break-words cursor-help" 
                    title={delivery.address.length > 18 ? delivery.address : undefined}
                  >
                    {delivery.address.length > 18 
                      ? `${delivery.address.substring(0, 18)}...` 
                      : delivery.address
                    }
                  </p>
                </div>
                {delivery.charge > 0 && (
                  <div className="flex items-center gap-2 mt-3 bg-brand-gold/10 border border-brand-gold/30 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-xs text-slate-600 font-montserrat">
                        Pago contra entrega
                      </p>
                      <p className="text-lg font-bold text-brand-primary font-montserrat">
                        S/ {delivery.charge.toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Menu Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const isDisabled = !item.available;
              
              return (
                <button
                  key={item.id}
                  onClick={item.onClick}
                  disabled={isDisabled}
                  className={`group rounded-2xl shadow-sm border p-6 transition-all duration-200 ${
                    isDisabled
                      ? 'bg-slate-50 border-slate-200 cursor-not-allowed opacity-60'
                      : 'bg-white border-slate-200 hover:shadow-lg hover:scale-[1.02] active:scale-95'
                  }`}
                  title={isDisabled ? item.disabledMessage : ''}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md transition-shadow duration-200 ${
                      isDisabled
                        ? 'bg-slate-300'
                        : `bg-gradient-to-br ${item.color} group-hover:shadow-lg`
                    }`}>
                      {isDisabled && item.id === 'Time' ? (
                        <Lock className="w-6 h-6 text-slate-500" />
                      ) : (
                        <IconComponent className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`text-base sm:text-lg font-semibold font-montserrat transition-colors duration-200 ${
                        isDisabled
                          ? 'text-slate-500'
                          : 'text-slate-900 group-hover:text-brand-primary'
                      }`}>
                        {item.label}
                      </p>
                      {isDisabled && item.disabledMessage && (
                        <p className="text-xs text-slate-500 font-montserrat mt-1">
                          {item.disabledMessage}
                        </p>
                      )}
                    </div>
                    {!isDisabled && (
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-brand-primary group-hover:translate-x-1 transition-all duration-200" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      </div>

    </div>
  );
}

export default FinalClientMain;